import React from "react";
import { CustomInput } from "@shared/src/components/common/form-components/CustomInput";
import { Button } from "@shared/src/components/ui/button";
import { ixToast } from "@shared/src/utils/ixToast";
import { settingsService } from "@shared/src/services/settings.service";
import { useMutation } from "@tanstack/react-query";
import { Spinner } from "@shared/src/components/common/misc/Spinner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@shared/src/components/ui/form";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import CustomDialogue from "@shared/src/components/common/misc/CustomDialogue";

const usernameSchema = z.object({
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(20, "Username must be less than 20 characters")
    .regex(/^[a-zA-Z0-9_-]+$/, "Username can only contain letters, numbers, underscores and hyphens"),
});

type FormValues = z.infer<typeof usernameSchema>;

interface UsernameDialogProps {
  username?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUsernameChanged?: (username: string) => void;
  title?: string;
  description?: string;
  preventClose?: boolean;
}

const UsernameDialog = ({
  username,
  isOpen,
  onOpenChange,
  onUsernameChanged,
  title,
  description,
  preventClose = false,
}: UsernameDialogProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(usernameSchema),
    defaultValues: {
      username: username || "",
    },
  });

  // Check username availability
  const checkUsernameMutation = useMutation({
    mutationFn: (username: string) => settingsService.checkUsername({ username }),
  });

  const { setActiveAccount, tempAuth } = useAuthStore();

  // Update username
  const updateUsernameMutation = useMutation({
    mutationFn: (username: string) => settingsService.updateUsername({ username }),
    onSuccess: async (data, username) => {
      ixToast.success("Username updated successfully");
      if (!tempAuth) {
        throw new Error("No sign in found");
      }
      if (!data.data) {
        throw new Error("No backend account found");
      }
      await setActiveAccount(
        {
          address: tempAuth.address,
          connectorIcon: tempAuth.connectorIcon,
          id: tempAuth.id,
          isAuthenticated: true,
          signer: tempAuth.signer,
          type: tempAuth.type,
          email: tempAuth.email,
          username: "",
          jwt: tempAuth.jwt,
          authParams: tempAuth.authParams,
          backendAccount: data.data,
        },
        true,
      );
      onOpenChange(false);
      onUsernameChanged?.(username);
    },
    onError: (error: any) => {
      ixToast.error(error.message || "Failed to update username");
    },
  });

  const onSubmit = async (data: FormValues) => {
    // First check username availability
    const checkResult = await checkUsernameMutation.mutateAsync(data.username);
    console.log(checkResult);

    if (checkResult.error) {
      form.setError("username", {
        type: "manual",
        message: checkResult.error || "Failed to check username availability",
      });
      return;
    }

    // If available, update username
    const res = await updateUsernameMutation.mutateAsync(data.username);
    if (res.error) {
      form.setError("username", {
        type: "manual",
        message: res.error || "Failed to update username",
      });
    }
  };

  const isLoading = checkUsernameMutation.isPending || updateUsernameMutation.isPending;

  return (
    <CustomDialogue
      preventClose={{ enabled: preventClose, hideCloseButton: true }}
      title={title || "Edit Username"}
      description={
        description || "*Username will also be applied to iExchange. Abusing them might lead to community penalties."
      }
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      {closeModal => (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col h-full">
            <div className="flex-1">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CustomInput
                        label="Username"
                        {...field}
                        error={!!form.formState.errors.username}
                        endIcon={isLoading ? <Spinner size={20} /> : null}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center justify-between gap-2 pt-4 mt-4">
              {!preventClose && (
                <Button
                  onClick={() => {
                    closeModal();
                    onOpenChange(false);
                  }}
                  variant="secondary"
                  size="lg"
                  radius="full"
                  width="flex"
                  type="button"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" variant="primary" size="lg" radius="full" width="flex" disabled={isLoading}>
                Save
              </Button>
            </div>
          </form>
        </Form>
      )}
    </CustomDialogue>
  );
};

export default UsernameDialog;
